import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface PageSpeed {
  id?: string;
  performance: number | null;
  seo: number | null;
  accessibility: number | null;
  best_practices: number | null;
  checked_at: string;
}

const ScoreCircle = ({ label, score }: { label: string; score: number | null }) => {
  const value = score ?? 0;
  const color = value >= 90 ? "hsl(142 71% 45%)" : value >= 50 ? "hsl(38 92% 50%)" : "hsl(var(--destructive))";
  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative h-[110px] w-[110px]">
        <svg width="110" height="110" viewBox="0 0 110 110">
          <circle cx="55" cy="55" r="40" fill="none" stroke="hsl(var(--border))" strokeWidth="8" />
          <circle
            cx="55"
            cy="55"
            r="40"
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform="rotate(-90 55 55)"
            style={{ transition: "stroke-dashoffset 0.8s ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-mono font-bold" style={{ color }}>{value}</span>
        </div>
      </div>
      <span className="text-center text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
    </div>
  );
};

const SpeedTab = ({ clientIds }: { clientIds: string[] }) => {
  const [speed, setSpeed] = useState<PageSpeed | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const clientId = clientIds[0];

  const fetchLatest = async () => {
    if (!clientId) return;
    const { data } = await supabase
      .from("page_speed")
      .select("*")
      .eq("client_id", clientId)
      .order("checked_at", { ascending: false })
      .limit(1);
    if (data && data.length > 0) setSpeed(data[0] as any);
  };

  useEffect(() => {
    if (!clientId) {
      setLoading(false);
      return;
    }
    (async () => {
      await fetchLatest();
      setLoading(false);
    })();
  }, [clientId]);

  const runCheck = async () => {
    if (!clientId || running) return;
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("check-page-speed", {
        body: { client_id: clientId },
      });
      if (error) throw error;
      const result = data?.results?.[0];
      if (result?.error) throw new Error(result.error);
      if (result) {
        setSpeed({
          performance: result.performance ?? null,
          seo: result.seo ?? null,
          accessibility: result.accessibility ?? null,
          best_practices: result.best_practices ?? null,
          checked_at: result.checked_at ?? new Date().toISOString(),
        });
      } else {
        await fetchLatest();
      }
      toast({ title: "Speed check complete" });
    } catch (err: any) {
      toast({ title: "Speed check failed", description: err.message, variant: "destructive" });
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-64 animate-pulse rounded-lg border border-border bg-muted" />
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-center">
        <p className="text-sm font-mono text-muted-foreground">No website connected yet.</p>
      </div>
    );
  }

  const RunButton = ({ label }: { label: string }) => (
    <button
      type="button"
      onClick={runCheck}
      disabled={running}
      className="inline-flex h-10 items-center justify-center rounded-[10px] bg-primary px-5 text-[13px] font-[700] text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-60"
    >
      {running ? "Running…" : label}
    </button>
  );

  if (!speed) {
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-[700] text-foreground">Page Speed</h1>
          <p className="text-sm text-muted-foreground">Lighthouse scores for your website.</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-10 text-center shadow-[0_8px_28px_hsl(var(--foreground)/0.08)]">
          <p className="mb-2 text-base font-[600] text-foreground">Run your first speed check</p>
          <p className="mb-6 text-sm text-muted-foreground">We'll measure Performance, SEO, Accessibility, and Best Practices.</p>
          <RunButton label="Run Check →" />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-[700] text-foreground">Page Speed</h1>
          <p className="text-sm text-muted-foreground">Lighthouse scores for your website.</p>
        </div>
        <RunButton label="Run Check" />
      </div>

      <div className="rounded-lg border border-border bg-card p-6 shadow-[0_8px_28px_hsl(var(--foreground)/0.08)]">
        <div className="grid grid-cols-2 justify-items-center gap-6 md:grid-cols-4">
          <ScoreCircle label="Performance" score={speed.performance} />
          <ScoreCircle label="SEO" score={speed.seo} />
          <ScoreCircle label="Accessibility" score={speed.accessibility} />
          <ScoreCircle label="Best Practices" score={speed.best_practices} />
        </div>
        {speed.checked_at && (
          <p className="mt-6 text-center text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
            Last checked: {new Date(speed.checked_at).toLocaleDateString()} at{" "}
            {new Date(speed.checked_at).toLocaleTimeString()}
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default SpeedTab;
